/**
 * The engine's parts on a page (the estimate card and the flow can sit in
 * different places, each in its own shadow root). Lookups search every part,
 * so the controller doesn't care which one an element ended up in.
 */
export class Parts {
  readonly roots: ShadowRoot[] = [];

  add(root: ShadowRoot): void {
    this.roots.push(root);
  }

  maybe<T extends HTMLElement = HTMLElement>(id: string): T | null {
    for (const root of this.roots) {
      const el = root.getElementById(id);
      if (el) return el as T;
    }
    return null;
  }

  /** An element the engine drew; a missing one is a bug in the markup. */
  el<T extends HTMLElement = HTMLElement>(id: string): T {
    const el = this.maybe<T>(id);
    if (!el) throw new Error(`quote engine: #${id} missing`);
    return el;
  }

  all<T extends Element = HTMLElement>(selector: string): T[] {
    return this.roots.flatMap((root) => Array.from(root.querySelectorAll<T>(selector)));
  }

  /** The focused element inside the engine, if any. */
  activeElement(): Element | null {
    for (const root of this.roots) if (root.activeElement) return root.activeElement;
    return null;
  }
}

export const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Scrolls an element into view below the host page's sticky header. */
export function scrollToEl(el: Element, stickyTop: number, block: 'start' | 'nearest' | 'center' = 'start'): void {
  if (block !== 'start') {
    el.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block });
    return;
  }
  const top = el.getBoundingClientRect().top + window.scrollY - stickyTop - 16;
  window.scrollTo({ top: Math.max(0, top), behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
}
